import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getNpInfo } from "../../../api/NonprofitInfo";
import { dashboard } from "../../../api/Dashboard";
import {
  refreshStripeUrl,
  retrieveStripeAccount,
  linkNewStripeAccount,
} from "../../../api/RefreshStripeLink";
import { updateNonprofitInfo } from "../../../api/UpdateNonprofitInfo";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { lineBreaks } from "../../shared/Utils/LineBreaks";
import Editor from "../CampaignModal/Editor/Editor";
import Spinner from "../../shared/LoadingSpinner/Spinner";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import PrimaryButton from "../../shared/Buttons/PrimaryButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faPen, faMagnifyingGlass, faSliders } from "@fortawesome/free-solid-svg-icons";

export default function NonprofitProfile() {
  const MAX_SIZE = 3 * 1024 * 1024;
  const [localUserData, setLocalUserData] = useState(null);
  const [npData, setNpData] = useState(null);
  const [refreshLink, setRefreshLink] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [profileFormEnabled, setProfileFormEnabled] = useState(false);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [showSaveTooltip, setShowSaveTooltip] = useState(false);
  const [nonprofitProfileFormData, setNonprofitProfileFormData] = useState({
    org_name: "",
    ein_num: "",
    address: {
      street: "",
      city: "",
      state: "",
      zip_code: "",
      country: "",
    },
    phone: "",
    mission_stmt: "",
    logo: null,
    instagram_url: "",
    facebook_url: "",
    website_url: "",
    amazon_wishlist_url: "",
    chewy_url: "",
    tiktok_url: "",
  });
  const [nonprofitProfileFormErrors, setNonprofitProfileFormErrors] = useState(
    {}
  );
  const [stripeModal, setStripeModal] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [stripeBannerDismissed, setStripeBannerDismissed] = useState(false);

  const [createError, setCreateError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploaded, setUploaded] = useState(false);

  const navigate = useNavigate();

  const fileInputRef = useRef(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(npData.stripe_account.id);
    setShowCopyTooltip(true);

    setTimeout(() => {
      setShowCopyTooltip(false);
    }, 1800);
  };

  useEffect(() => {
    const fetchUserDataAndNpData = async () => {
      try {
        // Fetch user data
        const userDataString = localStorage.getItem("userData");

        // If user data exists, format it and get NP data
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          const response = await dashboard(userData.id);
          setLocalUserData(response);

          // Get NP data
          if (response && response.nonprofit) {
            const npInfo = await getNpInfo(response.nonprofit);
            setNpData(npInfo);

            // If NP data exists, is approved by Admin, and does not have a stripe connect link, refresh the stripe URL
            // This allows NPs to connect their stripe account with us
            if (
              npInfo &&
              npInfo.is_approved &&
              npInfo.stripe_connect_link === ""
            ) {
              const url = await refreshStripeUrl(npInfo.id);
              setRefreshLink(url);
            } else if (
              npInfo &&
              npInfo.is_approved &&
              npInfo.stripe_connect_link !== ""
            ) {
              // Else get stripe account info (meaning NPs already set up their account with us)
              // Connects the stripe account to the NP in the backend
              const nonprofitStripeAccount = await retrieveStripeAccount(
                npInfo.id
              );
            }

            return npInfo;
          }
        }
      } catch (error) {
        console.error(
          "Failed to parse user data or fetch user profile: ",
          error
        );
        localStorage.removeItem("userData");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("accessToken");
        navigate("/");
      }
    };

    const fetchNonprofitProfileFormData = (tempNpData) => {
      return {
        org_name: tempNpData.org_name,
        ein_num: tempNpData.ein_num,
        address: {
          street: tempNpData.address.street,
          city: tempNpData.address.city,
          state: tempNpData.address.state,
          zip_code: tempNpData.address.zip_code,
          country: tempNpData.address.country,
        },
        phone: tempNpData.phone,
        mission_stmt: tempNpData.mission_stmt,
        logo: tempNpData.logo,
        instagram_url: tempNpData.instagram_url,
        facebook_url: tempNpData.facebook_url,
        website_url: tempNpData.website_url,
        amazon_wishlist_url: tempNpData.amazon_wishlist_url,
        chewy_url: tempNpData.chewy_url,
        tiktok_url: tempNpData.tiktok_url,
      };
    };

    const fetchAll = async () => {
      setIsLoading(true);
      const tempNpData = await fetchUserDataAndNpData();
      const tempNonprofieProfileFormData =
        fetchNonprofitProfileFormData(tempNpData);
      setNpData(tempNpData);
      setNonprofitProfileFormData(tempNonprofieProfileFormData);
      setIsLoading(false);
    };

    try {
      fetchAll();
    } catch (error) {
      console.error("Error in nonprofit profile", error);
    }
  }, [navigate]);

  // Stops the background from scrolling
  useEffect(() => {
    if (stripeModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [stripeModal]);

  const handleLinkDiffStripeAccount = async () => {
    try {
      setIsStripeLoading(true);
      const response = await linkNewStripeAccount(npData.id);
      if (response) {
        window.location.href = "/refresh-stripe-connect";
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      const formattedError = lineBreaks(errorMessage);
      console.error(error);
      setCreateError(formattedError);
    }
  };

  const handleNonprofitProfileInputChange = (e) => {
    const { name, value } = e.target;

    const regExURLs = {
      website_url: /^(|[^\s@]+\.[^\s@]+)$/,
    };

    if (regExURLs.website_url && !regExURLs.website_url.test(value)) {
      setNonprofitProfileFormErrors((prevErrors) => ({
        ...(prevErrors || {}),
        [name]: [`Invalid url format.`],
      }));
    } else {
      setNonprofitProfileFormErrors((prevErrors) => {
        const { [name]: removed, ...rest } = prevErrors || {};
        return rest;
      });
    }

    setNonprofitProfileFormData({
      ...nonprofitProfileFormData,
      [name]: value,
    });
  };

  const handleNonprofitAddressInputChange = (e) => {
    const { name, value } = e.target;
    setNonprofitProfileFormData({
      ...nonprofitProfileFormData,
      address: {
        ...nonprofitProfileFormData.address,
        [name]: value,
      },
    });
  };

  const handleNonprofitProfileFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();

      if (selectedFile) {
        submitData.append("logo", nonprofitProfileFormData.logo);
      }

      submitData.append("org_name", nonprofitProfileFormData.org_name);
      submitData.append("ein_num", nonprofitProfileFormData.ein_num);

      const addressObject = {
        street: nonprofitProfileFormData.address.street,
        city: nonprofitProfileFormData.address.city,
        state: nonprofitProfileFormData.address.state,
        zip_code: nonprofitProfileFormData.address.zip_code,
        country: nonprofitProfileFormData.address.country,
      };
      submitData.append("address", JSON.stringify(addressObject));
      submitData.append("phone", nonprofitProfileFormData.phone);
      submitData.append("mission_stmt", nonprofitProfileFormData.mission_stmt);

      const correctUrl = (url) => {
        if (url && !url.startsWith("https://") && !url.startsWith("http://")) {
          return "https://" + url;
        }
        return url;
      };

      if (nonprofitProfileFormData.instagram_url) {
        submitData.append(
          "instagram_url",
          correctUrl(nonprofitProfileFormData.instagram_url)
        );
      }
      if (nonprofitProfileFormData.facebook_url) {
        submitData.append(
          "facebook_url",
          correctUrl(nonprofitProfileFormData.facebook_url)
        );
      }
      if (nonprofitProfileFormData.website_url) {
        submitData.append(
          "website_url",
          correctUrl(nonprofitProfileFormData.website_url)
        );
      }
      if (nonprofitProfileFormData.amazon_wishlist_url) {
        submitData.append(
          "amazon_wishlist_url",
          correctUrl(nonprofitProfileFormData.amazon_wishlist_url)
        );
      }
      if (nonprofitProfileFormData.chewy_url) {
        submitData.append(
          "chewy_url",
          correctUrl(nonprofitProfileFormData.chewy_url)
        );
      }
      if (nonprofitProfileFormData.tiktok_url) {
        submitData.append(
          "tiktok_url",
          correctUrl(nonprofitProfileFormData.tiktok_url)
        );
      }
      submitData.append("tos", true);

      if (localUserData) {
        await updateNonprofitInfo(localUserData.nonprofit, submitData);
        setCreateError("");
        setUploaded(false);
        setShowSaveTooltip(true);
        setProfileFormEnabled(false);
        setNonprofitProfileFormErrors(null);
        setTimeout(() => {
          setShowSaveTooltip(false);
        }, 1800);
      } else {
        console.error("No local user data!");
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      const formattedError = lineBreaks(errorMessage);
      console.error(error);
      setCreateError(formattedError);
    }
  };

  const enableEdits = () => {
    setProfileFormEnabled(!profileFormEnabled);
  };

  const disableEdits = () => {
    setProfileFormEnabled(false);
  };

  if (isLoading) {
    return (
      <div className="flex flex-row ml-auto mt-[25vh] w-full h-full justify-center">
        <Spinner />
      </div>
    );
  }

  const handleEditorChange = (content, delta, source, editor) => {
    setNonprofitProfileFormData((prevState) => ({
      ...prevState,
      mission_stmt: content,
    }));
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_SIZE) {
        setCreateError("File size is too large (must be under 3MB)");
        event.target.value = "";
        setNonprofitProfileFormData({
          ...nonprofitProfileFormData,
          logo: null,
        });
      } else {
        setCreateError("");
        setNonprofitProfileFormData({
          ...nonprofitProfileFormData,
          logo: file,
        });
        setUploaded(true);
      }
    }
  };

  const handleChangeImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isPending = npData?.is_approved === false;
  const isApprovedNoStripe = npData?.is_approved === true && !npData?.stripe_account?.charges_enabled;

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-full text-sm placeholder-gray-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400";
  const labelClass = "block !text-sm !font-semibold text-gray-700 mb-1 text-left";
  const saveBtnClass = (disabled) =>
    `flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white bg-blue ${disabled ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`;
  const editBtnClass = (disabled) =>
    `flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white bg-blue-navy ${disabled ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`;

  return (
    <div className="flex-1 bg-gray-100 min-h-screen p-8">

      {/* Pending Banner */}
      {isPending && !bannerDismissed && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-blue" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1 text-left">
            <p className="!font-bold !text-lg text-gray-900 mb-2">Pending Status</p>
            <p className="!text-sm text-gray-700">
              We are currently reviewing your nonprofit information. You will be able to link your bank account with us once you are approved! Watch out for an email notification for your approval status.
            </p>
          </div>
          <button
            onClick={() => setBannerDismissed(true)}
            className="flex-shrink-0 text-gray-500 hover:text-gray-700 ml-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Stripe Onboarding Banner */}
      {isApprovedNoStripe && !stripeBannerDismissed && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-blue" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1 text-left">
            <p className="!font-bold !text-lg text-gray-900 mb-2">Complete Stripe Onboarding</p>
            <p className="!text-sm text-gray-700 mb-3">
              Your Stripe Onboarding is incomplete. To receive donations directly to your bank account, set up your Stripe Account in the 'Nonprofits Profile' Tab.
            </p>
            <button
              onClick={() => (window.location.href = "/refresh-stripe-connect")}
              className="!text-sm !font-bold text-gray-900 flex items-center gap-1 hover:underline"
            >
              Stripe Onboarding ↗
            </button>
          </div>
          <button
            onClick={() => setStripeBannerDismissed(true)}
            className="flex-shrink-0 text-gray-500 hover:text-gray-700 ml-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Title */}
      <h1 className="!text-[30px] !font-semibold !font-poppins text-blue-navy mb-4 text-left">
        Nonprofit Profile
      </h1>

      {/* Search + Filter */}
      <div className="flex items-center gap-3 mb-8 max-w-2xl">
        <div className="flex items-center gap-3 bg-white border border-gray-400 rounded-full px-5 py-[2px] flex-1">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400" size="sm" />
          <input
            type="text"
            placeholder="Search account details, stripe account, links"
            className="flex-1 outline-none border-none bg-transparent text-sm text-gray-700 placeholder-gray-400 py-2"
          />
        </div>
        <button className="flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap">
          <FontAwesomeIcon icon={faSliders} size="sm" />
          Filter
        </button>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-[1fr_320px] gap-6">

        {/* LEFT COLUMN */}
        <div className="flex flex-col gap-6">

          {/* Your Profile Card */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="!text-xl !font-bold text-gray-900">Your Profile</h2>
              <div className="flex gap-2">
                <button
                  onClick={!isPending ? handleNonprofitProfileFormSubmit : undefined}
                  disabled={isPending}
                  className={saveBtnClass(isPending)}
                >
                  <FontAwesomeIcon icon={faCheck} size="xs" /> Save
                </button>
                <button
                  onClick={!isPending ? enableEdits : undefined}
                  disabled={isPending}
                  className={editBtnClass(isPending)}
                >
                  <FontAwesomeIcon icon={faPen} size="xs" /> Edit
                </button>
              </div>
            </div>

            {/* Image row */}
            <div className="flex items-center gap-4 mb-5">
              <img
                src={selectedFile ? URL.createObjectURL(selectedFile) : npData?.logo}
                alt="Nonprofit"
                className="w-20 h-20 rounded-xl object-cover border border-gray-200 flex-shrink-0"
              />
              <div className="flex-1 text-left">
                <p className="!font-bold !text-sm text-gray-900 mb-0.5">Nonprofit Image</p>
                <p className="!text-xs text-gray-500">Upload an image</p>
              </div>
              <button
                onClick={!isPending ? handleChangeImageClick : undefined}
                disabled={isPending}
                className={`px-5 py-2 rounded-full text-sm font-semibold text-white bg-orange ${isPending ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
              >
                Upload
              </button>
              <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
            </div>

            {/* Mission Statement */}
            <div>
              <p className={`${labelClass} mb-2`}>Mission Statement</p>
              <div className="border border-gray-300 rounded-xl overflow-hidden">
                <Editor
                  defaultValue={nonprofitProfileFormData.mission_stmt}
                  onTextChange={handleEditorChange}
                  readOnly={!profileFormEnabled || isPending}
                  className="quill-editor"
                />
              </div>
            </div>
          </div>

          {/* Account Details Card */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="!text-xl !font-bold text-gray-900">Account Details</h2>
              <div className="flex gap-2">
                <button
                  onClick={!isPending ? handleNonprofitProfileFormSubmit : undefined}
                  disabled={isPending}
                  className={saveBtnClass(isPending)}
                >
                  <FontAwesomeIcon icon={faCheck} size="xs" /> Save
                </button>
                <button
                  onClick={!isPending ? enableEdits : undefined}
                  disabled={isPending}
                  className={editBtnClass(isPending)}
                >
                  <FontAwesomeIcon icon={faPen} size="xs" /> Edit
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {/* Org Name, Phone, EIN */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Organization Name</label>
                  <input type="text" name="org_name" value={nonprofitProfileFormData.org_name} onChange={handleNonprofitProfileInputChange} disabled={!profileFormEnabled || isPending} placeholder="Enter state" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Phone Number</label>
                  <input type="number" name="phone" value={nonprofitProfileFormData.phone} onChange={handleNonprofitProfileInputChange} disabled={!profileFormEnabled || isPending} placeholder="Enter country" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>EIN</label>
                  <input type="number" name="ein_num" value={nonprofitProfileFormData.ein_num} onChange={handleNonprofitProfileInputChange} disabled={!profileFormEnabled || isPending} placeholder="Enter zip code" className={inputClass} />
                </div>
              </div>

              {/* Street, City */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Street Address</label>
                  <input type="text" name="street" value={nonprofitProfileFormData.address.street} onChange={handleNonprofitAddressInputChange} disabled={!profileFormEnabled || isPending} placeholder="Enter street address" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  <input type="text" name="city" value={nonprofitProfileFormData.address.city} onChange={handleNonprofitAddressInputChange} disabled={!profileFormEnabled || isPending} placeholder="Enter City" className={inputClass} />
                </div>
              </div>

              {/* State, Country, Zip */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>State</label>
                  <select name="state" value={nonprofitProfileFormData.address.state} onChange={handleNonprofitAddressInputChange} disabled={!profileFormEnabled || isPending} className={`${inputClass} bg-white appearance-none`}>
                    <option value="">Enter state</option>
                    {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Country</label>
                  <select name="country" value={nonprofitProfileFormData.address.country} onChange={handleNonprofitAddressInputChange} disabled={!profileFormEnabled || isPending} className={`${inputClass} bg-white appearance-none`}>
                    <option value="US">US</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Zip Code</label>
                  <input type="text" name="zip_code" value={nonprofitProfileFormData.address.zip_code} onChange={handleNonprofitAddressInputChange} disabled={!profileFormEnabled || isPending} placeholder="Enter zip code" className={inputClass} />
                </div>
              </div>
            </div>

            {createError && <p className="text-red-500 text-sm mt-3">{createError}</p>}
            {showSaveTooltip && (
              <p className="text-green-500 text-sm mt-3">Information saved successfully!</p>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col gap-6">

          {/* Stripe Account Card */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="!text-xl !font-bold text-gray-900 mb-4 text-left">
            {npData?.stripe_account?.id ? "Stripe Account" : "Stripe Onboarding"}
          </h2>
            {isPending ? (
              <p className="!text-sm text-gray-400 text-left">Profile Pending</p>
            ) : npData?.stripe_account?.id ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={npData.stripe_account.id}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-full text-sm bg-gray-50 min-w-0"
                  />
                  <button
                    onClick={handleCopy}
                    className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold text-white bg-blue hover:opacity-90 whitespace-nowrap"
                  >
                    Copy ID
                  </button>
                </div>
                {showCopyTooltip && <p className="!text-xs text-green-500">Copied to clipboard!</p>}
                <button
                  onClick={() => setStripeModal(true)}
                  className="w-full py-2.5 rounded-full text-sm font-semibold text-white bg-orange hover:opacity-90"
                >
                  Reconnect Stripe Account
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="!text-sm text-gray-600 text-left">
                  We use Stripe to process payments, and you can connect to your bank account with us via Stripe.
                </p>
                <button
                  onClick={() => (window.location.href = "/refresh-stripe-connect")}
                  className="w-full py-2.5 rounded-full text-sm font-semibold text-white bg-orange hover:opacity-90"
                >
                  Complete Stripe Onboarding
                </button>
              </div>
            )}
          </div>

          {/* Links Card */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="!text-xl !font-bold text-gray-900">Links</h2>
              <div className="flex gap-2">
                <button
                  onClick={!isPending ? handleNonprofitProfileFormSubmit : undefined}
                  disabled={isPending}
                  className={saveBtnClass(isPending)}
                >
                  <FontAwesomeIcon icon={faCheck} size="xs" /> Save
                </button>
                <button
                  onClick={!isPending ? enableEdits : undefined}
                  disabled={isPending}
                  className={editBtnClass(isPending)}
                >
                  <FontAwesomeIcon icon={faPen} size="xs" /> Edit
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {[
                ["website_url", "Website", "Add website"],
                ["instagram_url", "Instagram", "Add instagram"],
                ["facebook_url", "Facebook", "Add facebook"],
                ["amazon_wishlist_url", "Amazon Wishlist", "Add amazon wishlist"],
                ["tiktok_url", "TikTok", "Add tiktok"],
                ["chewy_url", "Chewy", "Add Chewy"],
              ].map(([name, label, placeholder]) => (
                <div key={name}>
                  <label className={labelClass}>{label}</label>
                  <input
                    type="text"
                    name={name}
                    value={nonprofitProfileFormData[name]}
                    onChange={handleNonprofitProfileInputChange}
                    disabled={!profileFormEnabled || isPending}
                    placeholder={placeholder}
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stripe Reconnect Modal */}
      {stripeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            {isStripeLoading ? (
              <Spinner />
            ) : (
              <>
                <p className="mb-6 text-center text-lg font-normal">
                  This will disconnect your current Stripe account and initiate a new onboarding process. Would you like to continue?
                </p>
                <div className="flex justify-center gap-4">
                  <SecondaryButton onClick={handleLinkDiffStripeAccount}>Yes</SecondaryButton>
                  <PrimaryButton onClick={() => setStripeModal(false)}>No</PrimaryButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
